import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {useSelector} from 'react-redux';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {SettingSelectComponent} from '../../../../components/Settings/SettingSelectComponent';
import {useMap} from '../../../../hooks/map';
import {useFilter} from '../../../../hooks/filter';
import {SelectItem} from '../../../../components/Select';
import {LocationModel} from '../../../../types/location';
import {useRPC} from '../../../../hooks/rpc';
import {useMessage} from '../../../../hooks/message';
import {useHistory} from 'react-router-dom';
import {useLocalization} from "../../../../hooks/localization";

const BACK_URL = '/settings';

export const LocationEditScreen = memo(function LocationEditScreen() {
    const {t} = useLocalization();
    const {success, failure} = useMessage();
    const [rpc] = useRPC();
    const history = useHistory();

    const config = useSelector(state => state.control.config);
    const locationList = useSelector(state => state.control.locationList);

    const cityList: SelectItem<LocationModel>[] = useMap(
        useFilter(
            locationList,
            location => location.type === 'city',
            [],
        ), location => ({
            value: location,
            label: location.name,
        }), []);
    const [city, setCity] = useState<SelectItem<LocationModel>>(
        cityList.find(c => c.value.id === (config.location.type === 'city' ? config.location.id : config.location.parent_id)) || null,
    );

    const townList: SelectItem<LocationModel>[] = useMap(
        useFilter(
            locationList,
            location => location.type === 'town' && location.parent_id === city?.value?.id,
            [city?.value?.id],
        ), location => ({
            value: location,
            label: location.name,
        }), []);
    const [town, setTown] = useState<SelectItem<LocationModel>>(
        config.location.type === 'city' ? null :
            (townList.find(t => t.value.id === config.location.id) || null),
    );

    const onBackPressed = useCallback(() => {
        history.push(BACK_URL);
    }, [history]);

    const isFieldsValid = useCallback((): boolean => {
        if (city?.value == null) {
            failure(t('pls_select_a_city'));

            return;
        }

        return true;
    }, [t, city?.value, failure]);

    const onSavePressed = useCallback(() => {
        if (!isFieldsValid()) {
            return;
        }

        const location = town?.value || city?.value;
        return rpc.updateConfigs({...config, location})
            .then(() => {
                success(t('successfully_saved'));
                onBackPressed();
            })
            .catch(failure);
    }, [t, city?.value, config, failure, isFieldsValid, onBackPressed, rpc, success, town?.value]);

    const buttons = useMemo(() => [
        {
            text: t('save'),
            className: 'green',
            onClick: onSavePressed,
        },
        {
            text: t('cancel'),
            className: 'red',
            url: BACK_URL,
        },
    ], [t, onSavePressed]);

    useEffect(() => {
        setTown(townList.find(t => t.value.id === town?.value?.id) || null);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [townList]);

    return (
        <PasswordContainer requiredRole='admin' isFullscreen={true}>
            <SettingsPageContainer
                className='service-edit'
                buttons={buttons}
                backUrl={BACK_URL}>
                <div className='content-divider'>
                    <div className='content-wrapper'>
                        <SettingSelectComponent
                            label={t('city')}
                            options={cityList}
                            onChange={setCity}
                            value={city}/>
                        <SettingSelectComponent
                            label={t('town')}
                            options={townList}
                            onChange={setTown}
                            value={town}/>
                    </div>
                    <div className='content-wrapper'>

                    </div>
                </div>
            </SettingsPageContainer>
        </PasswordContainer>
    );
});
